<template>
  <v-container class="pa-0 product-inner-detail">
    <v-card flat class="transparent">
      <!-- Product Overview Header with Progress -->
      <div class="mb-8 pa-6 bg-white rounded-xl shadow-sm border d-flex align-center flex-wrap gap-6">
        <div class="flex-grow-1" style="min-width: 250px;">
          <div class="d-flex justify-space-between align-center mb-2">
            <div>
              <div class="caption grey--text font-weight-black text-uppercase tracking-wider">Avance del Producto</div>
              <div class="text-h4 font-weight-black primary--text">{{ Math.floor(productProgress) }}%</div>
            </div>
            <v-chip :color="productProgress >= 100 ? 'success' : 'accent'" label class="font-weight-black shadow-sm px-4">
              {{ productProgress >= 100 ? 'FINALIZADO' : 'EN PROCESO' }}
            </v-chip>
          </div>
          <v-progress-linear
            :value="productProgress"
            :color="productProgress >= 100 ? 'success' : 'accent'"
            height="12"
            rounded
            striped
            class="shadow-xs"
          />
        </div>
        
        <div v-if="product.notes" class="pa-4 bg-primary-light rounded-lg border-primary-light d-flex align-start flex-grow-1" style="max-width: 500px;">
          <v-icon small color="primary" class="mr-3 mt-1">fas fa-comment-dots</v-icon>
          <div>
            <div class="caption primary--text font-weight-black text-uppercase mb-1">Observaciones</div>
            <div class="body-2 grey--text text--darken-3 font-italic lh-1-4 text-left">{{product.notes}}</div>
          </div>
        </div>
      </div>

      <!-- Process Sequence Section -->
      <div class="mb-8">
        <div class="d-flex align-center mb-4 px-2">
          <v-avatar size="28" color="primary lighten-4" class="mr-3 shadow-sm">
            <v-icon x-small color="primary">fas fa-project-diagram</v-icon>
          </v-avatar>
          <span class="text-subtitle-1 font-weight-black grey--text text--darken-3">Flujo de Producción</span>
          <v-spacer></v-spacer>
          <v-chip x-small color="grey lighten-3" class="font-weight-bold grey--text text--darken-2">
            {{ product.process_orders ? product.process_orders.length : 0 }} Pasos Definidos
          </v-chip>
        </div>
        
        <v-card outlined class="modern-card overflow-hidden elevation-0">
          <v-simple-table class="modern-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-center" width="60">Pos.</th>
                  <th class="text-left">Proceso / Operación</th>
                  <th class="text-left">Estación de Trabajo</th>
                  <th class="text-left" width="220">Avance de Proceso</th>
                  <th class="text-center" width="130">Estado</th>
                  <th class="text-center" width="120" v-if="show_estimated_work_order">Estimado</th>
                  <th class="text-center" width="130">Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(process, index) in product.process_orders"
                  :key="index"
                  :class="process.completed ? 'bg-success-row' : ''"
                >
                  <td class="text-center caption grey--text font-weight-black">
                    {{index + 1}}
                    <v-tooltip top v-if="process.reprocess">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on" color="error" x-small class="ml-1">fas fa-history</v-icon>
                      </template>
                      <span>Reproceso detectado</span>
                    </v-tooltip>
                  </td>
                  <td>
                    <div class="body-2 font-weight-black primary--text text-left">{{process.name}}</div>
                    <div v-if="process.merma > 0" class="d-flex align-center mt-1">
                      <v-chip x-small color="orange lighten-4" label text-color="orange darken-3" class="font-weight-bold">
                        <v-icon x-small left>fas fa-percentage</v-icon>
                        {{process.merma}} Merma
                      </v-chip>
                    </div>
                  </td>
                  <td>
                    <div class="body-2 font-weight-medium grey--text text--darken-3">{{process.machine.name}}</div>
                    <div class="caption primary--text font-weight-bold d-flex align-center mt-1" v-if="process.equipment_asigned">
                      <v-icon x-small class="mr-1" color="primary">fas fa-tools</v-icon>
                      {{process.equipment_asigned}}
                    </div>
                  </td>
                  <td class="py-4">
                    <div v-bind="attrs" v-on="on" class="px-2">
                          <div class="d-flex justify-space-between caption mb-2">
                            <span class="grey--text font-weight-medium" v-if="process.machine.production_unit_m2">
                              <span class="font-weight-black primary--text">{{process.completed_mts2_qty.mts2 | number('0.00')}}</span> / {{product.mts2*process.qtypp | number('0.00')}} m²
                            </span>
                            <span class="grey--text font-weight-medium" v-else>
                              <span class="font-weight-black primary--text">{{process.completed_mts2_qty.qty | number('0.00')}}</span> / {{product.qty*process.qtypp+process.merma | number('0.00')}} un
                            </span>
                            <span class="font-weight-black" :class="process.completed ? 'success--text' : 'primary--text'">
                              {{ process.completed ? 100 : Math.round(process.machine.production_unit_m2 ? (process.completed_mts2_qty.mts2/(product.mts2*process.qtypp)*100) : (process.completed_mts2_qty.qty/(product.qty*process.qtypp+process.merma)*100)) }}%
                            </span>
                          </div>
                          <v-progress-linear
                            :color="process.completed ? 'success' : 'primary'"
                            height="6"
                            rounded
                            :value="process.completed ? 100 : (process.machine.production_unit_m2 ? (process.completed_mts2_qty.mts2/(product.mts2*process.qtypp)*100) : (process.completed_mts2_qty.qty/(product.qty*process.qtypp+process.merma)*100))" 
                            class="shadow-xs"
                          />
                        </div>
                  </td>
                  <td class="text-center">
                    <v-chip
                      v-if="process.completed"
                      label
                      x-small
                      color="success"
                      dark
                      class="font-weight-black px-2"
                    >
                      FINALIZADO
                    </v-chip>
                    <v-chip
                      v-else-if="product.in_prestage"
                      label
                      x-small
                      outlined
                      color="orange darken-2"
                      class="font-weight-black px-2"
                    >
                      PRE-PRENSA
                    </v-chip>
                    <v-chip
                      v-else
                      label
                      x-small
                      color="primary"
                      outlined
                      class="font-weight-black px-2"
                    >
                      PRODUCCIÓN
                    </v-chip>
                  </td>
                  <td class="text-center" v-if="show_estimated_work_order">
                    <div v-if="!process.completed" class="caption font-weight-bold grey--text text--darken-3">
                      {{process.estimated | moment("DD/MM/YYYY")}}
                    </div>
                    <v-icon v-else color="success" small>fas fa-check-circle</v-icon>
                  </td>
                  <td class="text-center">
                    <div class="d-flex justify-center gap-1">
                      <v-btn 
                        v-if="!process.completed" 
                        icon 
                        small 
                        color="primary" 
                        class="grey lighten-4 shadow-sm"
                        @click="addProcessRegistry(process)" 
                        :disabled="product.in_prestage"
                        title="Registrar Avance"
                      >
                        <v-icon x-small>fas fa-plus</v-icon>
                      </v-btn>
                      <v-btn 
                        v-else 
                        icon 
                        small 
                        color="error" 
                        class="grey lighten-4 shadow-sm"
                        @click="requestReprocess(process)" 
                        :disabled="product.in_prestage"
                        title="Solicitar Reproceso"
                      >
                        <v-icon x-small>fas fa-redo</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        small
                        color="info"
                        class="grey lighten-4 shadow-sm"
                        @click="showProcessBitacora(process)"
                        title="Ver Historial"
                      >
                        <v-icon x-small>fas fa-history</v-icon>
                      </v-btn>
                    </div>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
      </div>
     
      <!-- Materials Section -->
      <div class="mb-4">
        <div class="d-flex align-center mb-4 px-2">
          <v-avatar size="28" color="blue-grey lighten-4" class="mr-3 shadow-sm">
            <v-icon x-small color="blue-grey darken-3">fas fa-cubes</v-icon>
          </v-avatar>
          <span class="text-subtitle-1 font-weight-black grey--text text--darken-3">Insumos Requeridos</span>
         
        </div>
        
        <v-card outlined class="modern-card overflow-hidden elevation-0">
          <v-simple-table class="modern-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-center" width="60">Pos.</th>
                  <th class="text-left">Descripción del Material</th>
                  <th class="text-center" width="120">Cant. Unitaria</th>
                  <th class="text-center" width="120">Unidad</th>
                  <th class="text-right" width="150">Stock Necesario</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(supply, index) in product.supplies"
                  :key="index"
                >
                  <td class="text-center caption grey--text font-weight-black">{{index + 1}}</td>
                  <td>
                    <div class="body-2 font-weight-bold grey--text text--darken-3 text-left">{{supply.name}}</div>
                    <div v-show="supply.has_dimension" class="caption primary--text font-weight-medium mt-1">
                      <v-icon x-small color="primary" class="mr-1">fas fa-ruler-combined</v-icon>
                      {{supply.height}} x {{supply.width}}
                    </div>
                  </td>
                  <td class="text-center body-2 font-weight-medium">
                    <span v-if="supply.qty == 0" class="grey--text">—</span>
                    <span v-else>{{supply.qty | number('0.00')}}</span>
                  </td>
                  <td class="text-center">
                    <v-chip x-small label outlined color="grey darken-1" class="font-weight-black">
                      {{supply.unit_name ? supply.unit_name.toUpperCase() : 'UND'}}
                    </v-chip>
                  </td>
                  <td class="text-right">
                    <span v-if="supply.qty == 0" class="grey--text">—</span>
                    <template v-else>
                      <span v-if="supply.unit == 1" class="body-2 font-weight-black primary--text">{{supply.qty*product.mts2 | number('0.00')}}</span>
                      <span v-else-if="supply.unit == 2" class="body-2 font-weight-black primary--text">{{Math.ceil((supply.qty*product.qty).toFixed(3))}}</span>
                      <span v-else class="body-2 font-weight-black primary--text">{{supply.qty | number('0.00')}}</span>
                    </template>
                  </td>
                </tr>
                <tr v-if="!product.supplies || product.supplies.length === 0">
                  <td colspan="5" class="text-center py-6 grey--text caption">No hay materiales definidos para este producto</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
      </div>

      <!-- Consumption Detail Component -->
      <v-row class="mt-4">
        <v-col cols="12">
          <product-consumption-detail ref="consumptionDetail" :productId="this.product.id" :work_order_id="work_order_id" />
        </v-col>
      </v-row>
    </v-card>

    <!-- Dialogs -->
    <v-dialog v-model="addingSupply" max-width="800" persistent>
      <v-card outlined class="modern-card overflow-hidden">
        <v-sheet color="primary" class="pa-4 d-flex align-center">
          <v-avatar size="32" color="white opacity-20" class="mr-3">
            <v-icon color="white" small>fas fa-plus-circle</v-icon>
          </v-avatar>
          <span class="text-h6 white--text font-weight-bold">Agregar Material al Producto</span>
          <v-spacer></v-spacer>
          <v-btn icon color="white" @click="addingSupply = false"><v-icon small>fas fa-times</v-icon></v-btn>
        </v-sheet>
        <v-card-text class="pa-6">
          <add-cost @add="addSupply" :is_supply="true"/>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="addingSupplyConsumption" max-width="450" persistent>
      <v-card outlined class="modern-card overflow-hidden">
        <v-sheet color="success" class="pa-6 d-flex align-center">
          <v-avatar size="44" color="white opacity-20" class="mr-4 shadow-sm">
            <v-icon color="white">fas fa-box-open</v-icon>
          </v-avatar>
          <div>
            <div class="caption white--text opacity-80 font-weight-black text-uppercase tracking-widest mb-1">Logística de Producción</div>
            <div class="text-h5 white--text font-weight-black lh-1-2">Registrar Consumo</div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon color="white" large @click="addingSupplyConsumption = false">
            <v-icon>fas fa-times</v-icon>
          </v-btn>
        </v-sheet>
        <v-card-text class="pa-0">
          <add-consumption 
            v-if="supplyToConsume" 
            :productId="this.product.id" 
            :work_order_id="work_order_id" 
            :article="supplyToConsume" 
            @added="consumptionAdded"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="dialog_process_completed"
      :max-width="machineToComplete.hour_control ? 1200 : 800"
      persistent
      transition="dialog-bottom-transition"
    >
      <v-card outlined class="modern-card overflow-hidden">
        <process-complete-conventional 
          :processes="[processToComplete]" 
          :machine="machineToComplete"
          @refresh="$emit('updated')"
          @close="dialog_process_completed = false"
        />
      </v-card>
    </v-dialog>

    <v-dialog v-model="showBitacoraDialog" max-width="800">
      <v-card outlined class="modern-card overflow-hidden">
        <v-sheet color="primary" class="pa-6 d-flex align-center flex-wrap">
          <div class="d-flex align-center w-100">
            <v-avatar size="44" color="white opacity-20" class="mr-4 shadow-sm">
              <v-icon color="white">fas fa-history</v-icon>
            </v-avatar>
            <div class="white--text">
              <div class="caption white--text opacity-80 font-weight-black text-uppercase tracking-widest mb-1">Historial de Producción</div>
              <div v-if="processToReview" class="text-h5 font-weight-black lh-1-2">
                OT #{{processToReview.work_order}} <span class="mx-2 opacity-50">•</span> {{processToReview.name}}
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon color="white" large @click="showBitacoraDialog = false">
              <v-icon>fas fa-times</v-icon>
            </v-btn>
          </div>
          
          <v-col cols="12" class="pa-0 mt-4" v-if="processToReview && processToReview.product">
            <v-sheet color="white opacity-10" class="pa-3 rounded-lg d-flex align-center border-white-10">
              <v-icon x-small color="white" class="mr-3 opacity-70">fas fa-box-open</v-icon>
              <div class="d-flex align-center flex-wrap">
                <span class="caption white--text font-weight-black opacity-70 mr-2">[{{processToReview.product.library_product.sku}}]</span>
                <span class="body-2 white--text font-weight-medium">{{processToReview.product.name}}</span>
              </div>
            </v-sheet>
          </v-col>
        </v-sheet>
        <v-card-text class="pa-0">
          <bitacora-details v-if="processToReview" :process="processToReview" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <confirm
      v-if="processToReprocess"
      v-model="showConfirmReprocess" 
      :message="`¿Confirma el registro de REPROCESO para ${processToReprocess.name}? Esta acción registrará la incidencia en el historial.`"
      @ok="registerReprocess(processToReprocess)"
      @cancel="() => { showConfirmReprocess = false; processToReprocess = {} }"
    />
  </v-container>
</template>

<script>
import AddCost from '@/components/products/costs/AddCost'
import SupplyConsume from '@/components/production/SupplyConsume.vue'
import {createSupplyOrder,deleteSupplyOrder,reprocess} from '@/api/production'
import notifications from '@/utils/notifications'
import BitacoraDetails from '@/components/production/BitacoraDetails'
import ProcessCompleteConventional from '@/components/production/ProcessCompleteConventional'
import ProductConsumptionDetail from './ProductConsumptionDetail.vue'
import AddConsumption from './AddConsumption.vue'

export default {
  name: 'ProductDetail',
  components: { SupplyConsume, AddCost, BitacoraDetails, ProcessCompleteConventional, ProductConsumptionDetail, AddConsumption },
  props: {
    product: { type: Object },
    work_order_id: {}
  },
  data() {
    return {
      addingSupplyConsumption: false,
      supplyToConsume: '',
      addingSupply: false,
      addingSupplyQty: false,
      dialog_process_completed: false,
      processToComplete: {},
      processToReprocess: {},
      machineToComplete: {},
      newSupply: { qty: 0 },
      showConfirmReprocess: false,
      showBitacoraDialog: false,
      processToReview: null,
      loading: false,
    }
  },
  computed: {
    show_estimated_work_order() {
      return this.$store.getters.getCompany.show_estimated_work_order
    },
    productProgress() {
      if (!this.product || !this.product.process_orders || this.product.process_orders.length === 0) return 0
      
      const totalProgress = this.product.process_orders.reduce((acc, proc) => {
        if (proc.completed) return acc + 100
        
        const target = proc.machine.production_unit_m2 
          ? (this.product.mts2 * proc.qtypp) 
          : (this.product.qty * proc.qtypp + (proc.merma || 0))
        
        const current = proc.machine.production_unit_m2 
          ? (proc.completed_mts2_qty.mts2 || 0) 
          : (proc.completed_mts2_qty.qty || 0)
        
        const procPercent = target > 0 ? (current / target) * 100 : 0
        return acc + Math.min(100, procPercent)
      }, 0)
      
      return totalProgress / this.product.process_orders.length
    }
  },
  methods: {
    async addSupply(item) {
      this.addingSupply = false
      this.newSupply = item
      this.loading = true
      let payload = {
        product: this.product.id,
        group: this.newSupply.group,
        name: this.newSupply.name,
        sku: this.newSupply.sku ? this.newSupply.sku : '',
        unit: this.newSupply.unit,
        qty: 0,
        price: this.newSupply.price,
        min_price: this.newSupply.min_price,
        additional: true,
      }
      if (item.has_article) {
        payload = { ...payload, article_id: item.article.id }
      }
      try {
        await createSupplyOrder(payload)
        notifications.showSuccess('Material agregado exitosamente')
        this.addingSupplyQty = false
        this.$emit('updated')
      } catch (e) {
        notifications.showError('Error al agregar material: ' + e)
      } finally {
        this.loading = false
      }
    },
    async deleteSupply(item) {
      this.loading = true
      try {
        await deleteSupplyOrder(item.id)
        notifications.showSuccess('Material eliminado')
        this.$emit('updated')
      } catch (e) {
        notifications.showError('Error al eliminar material: ' + e)
      } finally {
        this.loading = false
      }
    },
    addProcessRegistry(item) {
      const process = { ...item }
      this.$set(process, 'work_order', this.work_order_id)
      this.$set(process, 'product', this.product)
      this.machineToComplete = item.machine
      this.processToComplete = process
      this.$set(this.machineToComplete, 'equipments', process.machine_equipments)
      this.dialog_process_completed = true
    },
    requestReprocess(item) {
      this.processToReprocess = item
      this.showConfirmReprocess = true
    },
    async registerReprocess(item) {
      this.loading = true
      this.showConfirmReprocess = false
      try {
        await reprocess(item.id)
        notifications.showSuccess('Reproceso registrado correctamente')
        this.$emit('updated')
      } catch (e) {
        notifications.showError('Error al registrar reproceso: ' + e)
      } finally {
        this.loading = false
        this.processToReprocess = {}
      }
    },
    showProcessBitacora(process) {
      this.processToReview = process
      this.showBitacoraDialog = true
    },
    consumeSupply(supply) {
      if (supply.article_id) {
        // Formateo el objeto para que AddConsumption lo entienda (similar a lo que devuelve AddArticle)
        this.supplyToConsume = {
          id: supply.article_id,
          sku: supply.name.split(']')[0].replace('[',''), // Intento extraer SKU si existe en el nombre
          name: supply.name,
          unit_name: supply.unit_name,
          unit_consume_name: supply.unit_name,
          unit_consume_factor: 1,
          avg: 0 // No tenemos el precio aquí, pero AddConsumption lo necesita
        }
        this.addingSupplyConsumption = true
      }
    },
    consumptionAdded() {
      this.addingSupplyConsumption = false
      // Refresco inmediato de la lista local de consumos
      if (this.$refs.consumptionDetail) {
        this.$refs.consumptionDetail.fetch()
      }
      // Notifico al padre para actualizar indicadores globales (avance, m2, etc)
      this.$emit('updated')
    }
  }
}
</script>

<style scoped>
.modern-card {
  border: 1px solid rgba(0,0,0,0.08) !important;
  border-radius: 12px !important;
}
.bg-primary-light { background-color: rgba(var(--v-primary-base), 0.05); }
.border-primary-light { border: 1px solid rgba(var(--v-primary-base), 0.1); }
.bg-success-row { background-color: rgba(76, 175, 80, 0.04) !important; }
.shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.05) !important; }
.shadow-sm { box-shadow: 0 2px 4px rgba(0,0,0,0.08) !important; }
.lh-1-4 { line-height: 1.4; }
.gap-1 { gap: 4px; }
.product-inner-detail >>> .v-data-table { background: transparent !important; }
.white-opacity-10 { background-color: rgba(255, 255, 255, 0.1) !important; }
.border-white-10 { border: 1px solid rgba(255, 255, 255, 0.1); }
.opacity-80 { opacity: 0.8; }
.tracking-widest { letter-spacing: 2px; }
.w-100 { width: 100%; }
</style>